// Step-snap visual smoother (de_outpost v6 Phase B4 P8).
//
// Server collision applies a discrete step-up of up to stepHeight=0.45m in a
// single tick when the player walks onto a stair / ledge (see
// server/internal/physics/collision.go). Without smoothing the camera and
// remote-player Y position jumps in sync, which reads as a visible "pop" at
// every stair. This module rides on top of the authoritative Y stream and
// emits a smoothed display value: when a positive Y delta in the legal
// step-up range arrives, the next STEP_SNAP_TICKS authoritative samples are
// blended in with `displayY = lerp(prevDisplay, serverY, STEP_SNAP_ALPHA)`,
// then the display snaps back to serverY so it can never lag.
//
// Spec source: new_map/research/physics_findings.md §5.3.
//
// Behaviour outside the smoothing window is exact pass-through — sub-threshold
// jitter, downward steps, and large teleports (respawn / mantle / ladder) all
// bypass the lerp so the smoother never fights another system.

import { lerp } from '../utils/math';

/** Minimum positive ΔY (in metres) treated as a step event. Below this we
 *  assume sub-pixel jitter and pass through. */
export const STEP_SNAP_MIN_DELTA_M = 0.05;

/** Maximum ΔY (in metres) treated as a step event. Anything larger is a
 *  teleport (respawn, mantle, ladder dismount) and snaps without smoothing —
 *  smoothing a teleport would float the camera through walls. Sized to
 *  comfortably cover the server stepHeight of 0.45 m. */
export const STEP_SNAP_MAX_DELTA_M = 0.5;

/** Number of authoritative ticks the smoother holds the lerp open after a
 *  step event. The final tick force-snaps to serverY. */
export const STEP_SNAP_TICKS = 5;

/** Per-tick lerp alpha during the smoothing window. */
export const STEP_SNAP_ALPHA = 0.2;

export class StepSnapSmoother {
  private prevServerY: number = NaN;
  private displayY: number = NaN;
  private ticksRemaining = 0;

  /** Force-reseed the smoother (e.g. on respawn, map load). After reset the
   *  next update(y) emits y unchanged. */
  reset(y: number): void {
    this.prevServerY = y;
    this.displayY = y;
    this.ticksRemaining = 0;
  }

  /** Push the latest authoritative Y and return the value to render. */
  update(serverY: number): number {
    if (Number.isNaN(this.prevServerY)) {
      this.prevServerY = serverY;
      this.displayY = serverY;
      return serverY;
    }

    const delta = serverY - this.prevServerY;
    if (delta > STEP_SNAP_MIN_DELTA_M && delta <= STEP_SNAP_MAX_DELTA_M) {
      this.ticksRemaining = STEP_SNAP_TICKS;
    }

    if (this.ticksRemaining > 0) {
      this.ticksRemaining -= 1;
      if (this.ticksRemaining === 0) {
        // Final tick of the window — snap so display never permanently lags.
        this.displayY = serverY;
      } else {
        this.displayY = lerp(this.displayY, serverY, STEP_SNAP_ALPHA);
      }
    } else {
      this.displayY = serverY;
    }

    this.prevServerY = serverY;
    return this.displayY;
  }
}
